import {
    CoreButtonView,
    CoreLayoutView,
    CoreTextView,
    TCoreButtonStyleProps,
    type TCoreButtonViewProps,
    TCoreLayoutStyleProps,
    TCoreTextStyleProps,
} from '@core/widgets';
import { TViewCommonOption } from '@libs/types';
import React, { ElementType, MouseEventHandler } from 'react';
import { TRowOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import { Tag } from '../UI/Tag/Tag';
import styles from './styles/rowEmphasised.module.css';

export type TRowEmphasisedViewProps = {
    /** Used solely for theme resolution inside core views. When omitted, a minimal widget is synthesized from `uid`. */
    widget?: TCoreButtonViewProps['widget'];
    uid?: string;
    title?: string;
    subtitle?: string;
    meta?: string;
    iconName?: string;
    avatar?: string;
    metaVariant?: TRowOption['metaVariant'];
    isContentDisabled?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    as?: ElementType;
    children?: React.ReactNode;
    /** Design version of the widget it renders for (`TWidget.version`): picks semantic colors and icons. */
    version: number | undefined;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'margin' | 'parentStyleNames'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--white)',
    outline: 'var(--grey_40_lc)',
    borderStroke: 1,
    borderRadius: 16,
    padding: { top: 24, right: 24, bottom: 24, left: 24 },
    gap: 16,
    fillContainer: true,
    cursorDisabled: 'default',
    textAlign: 'start',
    hovered: {
        main: 'var(--grey_16_lc)',
    },
    pressed: {
        main: 'var(--grey_8_lc)',
    },
    disabled: {
        main: 'var(--white)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    textAlign: 'start',
    main: 'var(--black)',
    disabled: {
        main: 'var(--grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    textAlign: 'start',
    main: 'var(--grey_72_lc)',
    disabled: {
        main: 'var(--grey_40_lc)',
    },
};

const META_LAYOUT_STYLE_PROPS: TCoreLayoutStyleProps = {
    flexShrink: 0,
};

export const RowEmphasisedView = ({
    widget,
    uid,
    title,
    subtitle,
    meta,
    iconName,
    avatar,
    metaVariant,
    isDisabled,
    isContentDisabled,
    isHide,
    onClick,
    as = 'button',
    margin,
    children,
    parentStyleNames = [],
    version,
}: TRowEmphasisedViewProps) => {
    const resolvedWidget = widget ?? ({ uid: uid ?? '', data: {}, options: {} } as TCoreButtonViewProps['widget']);
    const contentDisabled = isContentDisabled ?? isDisabled ?? false;

    return (
        <CoreButtonView
            widget={resolvedWidget}
            text={title}
            isDisabled={isDisabled}
            isHide={isHide}
            onClick={onClick}
            as={as}
            parentStyleNames={parentStyleNames}
            styleName="rowEmphasised"
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin,
            }}
        >
            {(iconName || avatar) && (
                <VisualView
                    version={version}
                    uid={`icon-${resolvedWidget.uid}`}
                    avatar={avatar}
                    iconName={iconName}
                    iconHierarchy={'secondary'}
                    iconVariant={'circle'}
                    size={'small'}
                    isDisabled={contentDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="rowEmphasisedIcon"
                />
            )}
            <div className={styles.rowContent}>
                <div className={styles.row} data-test={`title-${resolvedWidget.uid}`}>
                    <CoreTextView
                        text={title}
                        styleProps={TITLE_STYLE_PROPS}
                        isDisabled={contentDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowEmphasisedTitle"
                    />
                </div>
                {subtitle && (
                    <div className={styles.row} data-test={`subtitle-${resolvedWidget.uid}`}>
                        <CoreTextView
                            text={subtitle}
                            styleProps={SUBTITLE_STYLE_PROPS}
                            isDisabled={contentDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowEmphasisedSubtitle"
                        />
                    </div>
                )}
            </div>
            {meta && (
                <CoreLayoutView
                    uid={`meta-${resolvedWidget.uid}`}
                    isDisabled={contentDisabled}
                    styleName="rowEmphasisedMetaLayout"
                    parentStyleNames={parentStyleNames}
                    styleProps={META_LAYOUT_STYLE_PROPS}
                    style={{ width: 'fit-content' }}
                >
                    <Tag
                        metaText={meta}
                        metaVariant={metaVariant}
                        metaHierarchy={metaVariant === 'tag' ? 'secondary' : undefined}
                        isDisabled={contentDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowEmphasisedMetaText"
                        version={version}
                    />
                </CoreLayoutView>
            )}
            {children}
        </CoreButtonView>
    );
};
